// Portfolio Administration v1.0 PR 7 — TanStack Query hooks for the
// /portfolio-admin/metrics dashboard. (The failure-tracking hooks retired
// with their page — default cases now surface on the Defaults & Workout
// cockpit; LoanFailure rows auto-resolve when their condition clears.)
//
// Metrics endpoints are server-cached for 60s; we keep React Query stale
// time aligned (staleTime: 60s) so the UI doesn't fetch faster than the
// upstream cache can refresh.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { portfolioMetricsApi } from "@/api/portfolioAdmin/pr7Client";

const STALE_60S = 60 * 1000;

export const pr7Keys = {
  composition: ["portfolio-admin", "metrics", "composition"] as const,
  performance: ["portfolio-admin", "metrics", "performance"] as const,
  operationalHealth: [
    "portfolio-admin",
    "metrics",
    "operational-health",
  ] as const,
  financial: ["portfolio-admin", "metrics", "financial"] as const,
};

export function useMetricsComposition() {
  return useQuery({
    queryKey: pr7Keys.composition,
    queryFn: () => portfolioMetricsApi.composition(),
    staleTime: STALE_60S,
  });
}

export function useMetricsPerformance() {
  return useQuery({
    queryKey: pr7Keys.performance,
    queryFn: () => portfolioMetricsApi.performance(),
    staleTime: STALE_60S,
  });
}

export function useMetricsOperationalHealth() {
  return useQuery({
    queryKey: pr7Keys.operationalHealth,
    queryFn: () => portfolioMetricsApi.operationalHealth(),
    staleTime: STALE_60S,
  });
}

export function useMetricsFinancial() {
  return useQuery({
    queryKey: pr7Keys.financial,
    queryFn: () => portfolioMetricsApi.financial(),
    staleTime: STALE_60S,
  });
}

export function useRefreshAllMetrics() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["portfolio-admin", "metrics"] });
  };
}
